import { createContext, useContext, useEffect, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { Link } from 'react-router'
import { useTranslation } from 'react-i18next'
import { ChevronLeft } from 'lucide-react'
import { buttonVariants } from '@/components/ui/button'
import { cn } from '@/lib/utils'

/**
 * Mobile app chrome (DECISIONS D-026). The shell owns a sticky top bar slot and the bottom tab bar;
 * each screen fills the bar with <ScreenHeader> (title, back, actions) and may hide the tab bar
 * (forms, focused flows). Outside a shell (auth, technician) the header renders in place.
 */
interface Chrome {
  slot: HTMLElement | null
  setTabBarHidden: (hidden: boolean) => void
}

const ChromeContext = createContext<Chrome | null>(null)

export function ChromeProvider({ slot, onTabBarHidden, children }: { slot: HTMLElement | null; onTabBarHidden: (hidden: boolean) => void; children: ReactNode }) {
  return <ChromeContext.Provider value={{ slot, setTabBarHidden: onTabBarHidden }}>{children}</ChromeContext.Provider>
}

/** Hides the bottom tab bar while the calling screen is mounted (forms with a sticky action bar). */
export function useHideTabBar(hidden = true) {
  const chrome = useContext(ChromeContext)
  useEffect(() => {
    if (!chrome || !hidden) return
    chrome.setTabBarHidden(true)
    return () => chrome.setTabBarHidden(false)
  }, [chrome, hidden])
}

interface ScreenHeaderProps {
  /** Screen title: the page's h1. */
  title: string
  /** Where "back" goes. Tab roots have no back. */
  back?: string | undefined
  /** Trailing icon buttons (≤ 2): "+", "⋯", save. */
  actions?: ReactNode
  /** Leading content instead of back (tab roots: household switcher). */
  leading?: ReactNode
  /**
   * Tab roots show the title large in the content (iOS large title) and keep the bar for
   * switcher/actions; pushed screens show a compact title in the bar.
   */
  large?: boolean
  /** Forms and focused flows: no tab bar, the screen owns the bottom edge. */
  hideTabBar?: boolean
  /** Optional line under the large title. */
  subtitle?: ReactNode
  /** Interactive content for the large title (e.g. the household switcher); `title` stays its name. */
  titleContent?: ReactNode
}

export function BackButton({ to }: { to: string }) {
  const { t } = useTranslation()
  return (
    <Link to={to} aria-label={t('common.back')} className={cn(buttonVariants({ variant: 'ghost', size: 'icon' }), '-ml-2 text-action')}>
      <ChevronLeft aria-hidden className="size-7" />
    </Link>
  )
}

function Bar({ title, back, actions, leading, large }: ScreenHeaderProps) {
  return (
    <div className="mx-auto flex min-h-14 w-full max-w-page items-center gap-1 px-2 sm:px-4 lg:px-6">
      <div className="flex min-w-0 flex-1 items-center gap-1">
        {leading ?? (back ? <BackButton to={back} /> : null)}
        {!large ? (
          <h1 className={cn('min-w-0 truncate text-title font-semibold', !back && !leading && 'pl-2')}>{title}</h1>
        ) : null}
      </div>
      {actions ? <div className="flex shrink-0 items-center gap-1">{actions}</div> : null}
    </div>
  )
}

export function ScreenHeader(props: ScreenHeaderProps) {
  const chrome = useContext(ChromeContext)
  useHideTabBar(Boolean(props.hideTabBar))
  const bar = <Bar {...props} />
  return (
    <>
      {chrome?.slot ? createPortal(bar, chrome.slot) : chrome ? null : <header className="-mx-4 -mt-2 mb-2">{bar}</header>}
      {props.large ? (
        <div className="space-y-1 pt-1">
          <h1 className="break-words text-large font-bold tracking-tight">{props.titleContent ?? props.title}</h1>
          {props.subtitle ? <p className="text-small text-fg-muted">{props.subtitle}</p> : null}
        </div>
      ) : null}
    </>
  )
}
